import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Music, Upload as UploadIcon, Video as VideoIcon, Search, Loader2, Play, Pause, Check } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/** Track URLs from /api/music/library come back as relative `/api/...` paths.
 *  Resolve them against the backend so <audio> and fetch() work in dev + prod. */
const resolveTrackUrl = (u) => {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  return u.startsWith("/") ? `${BACKEND}${u}` : u;
};

/**
 * Compound audio picker supporting three sources:
 *  - Upload:  local audio file
 *  - Extract: pick a video file and extract its audio track client-side using
 *             MediaRecorder + <video>.captureStream(). Produces a WebM/Opus blob.
 *  - Library: pick a track from the curated /api/music/library. The URL is
 *             fetched, converted to a Blob, wrapped in a File-like object, and
 *             handed off to the parent — same interface as an uploaded file.
 *
 * Props:
 *  - onPick(fileLikeOrNull, label?) — called with the chosen audio file (or
 *                                     null when cleared).
 */
export default function AudioPicker({ onPick }) {
  const [tab, setTab] = useState("upload"); // "upload" | "extract" | "library"

  return (
    <div>
      {/* Tabs */}
      <div
        data-testid="audio-picker-tabs"
        className="grid grid-cols-3 gap-1 border border-white/10 p-1 mb-3"
      >
        {[
          { id: "upload", label: "upload", Icon: UploadIcon },
          { id: "extract", label: "extract", Icon: VideoIcon },
          { id: "library", label: "library", Icon: Music },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`audio-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`py-2 flex items-center justify-center gap-2 transition-all ${
              tab === t.id
                ? "bg-[#00FFFF]/15 text-[#00FFFF] shadow-[0_0_10px_rgba(0,255,255,0.25)]"
                : "text-white/50 hover:text-white"
            }`}
          >
            <t.Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
            <span className="font-mono text-[10px] uppercase tracking-widest">
              {t.label}
            </span>
          </button>
        ))}
      </div>

      {tab === "upload" && <UploadTab onPick={onPick} />}
      {tab === "extract" && <ExtractTab onPick={onPick} />}
      {tab === "library" && <LibraryTab onPick={onPick} />}
    </div>
  );
}

/* ---------------------------- Upload tab ---------------------------- */
function UploadTab({ onPick }) {
  const inputRef = useRef(null);
  const handle = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("audio/")) {
      toast.error("Please choose an audio file");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("Track too large (max 25 MB)");
      return;
    }
    onPick(f, f.name);
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        data-testid="upload-audio-input"
        onChange={handle}
      />
      <button
        type="button"
        data-testid="upload-audio-picker"
        onClick={() => inputRef.current?.click()}
        className="w-full border-2 border-dashed border-white/15 hover:border-[#00FFFF]/60 hover:bg-[#00FFFF]/5 py-6 flex flex-col items-center gap-1 transition-all group"
      >
        <Music className="h-6 w-6 text-white/40 group-hover:text-[#00FFFF]" strokeWidth={1.5} />
        <p className="text-white/60 text-sm">add a music track</p>
        <p className="font-mono text-[10px] uppercase tracking-widest text-white/30">
          mp3 · m4a · aac · wav · max 25 mb
        </p>
      </button>
    </>
  );
}

/* ---------------------------- Extract tab ---------------------------- */
function ExtractTab({ onPick }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const extract = async (file) => {
    setBusy(true);
    setProgress(0);
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.src = url;
      video.muted = false;
      video.crossOrigin = "anonymous";
      await new Promise((resolve, reject) => {
        video.addEventListener("loadedmetadata", resolve, { once: true });
        video.addEventListener("error", () => reject(new Error("Cannot load video")), { once: true });
      });
      const stream = typeof video.captureStream === "function"
        ? video.captureStream()
        : video.mozCaptureStream?.();
      if (!stream) throw new Error("Your browser can't capture the video stream");
      const audioTracks = stream.getAudioTracks();
      if (!audioTracks.length) throw new Error("This video has no audio track");
      const audioStream = new MediaStream(audioTracks);

      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "audio/webm";
      const recorder = new MediaRecorder(audioStream, { mimeType: mime });
      const chunks = [];
      recorder.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
      recorder.start(500);
      await video.play();
      const duration = Number.isFinite(video.duration) ? video.duration : 30;
      const tick = () => {
        if (!video.ended && video.currentTime < duration) {
          setProgress(Math.min(1, video.currentTime / duration));
          requestAnimationFrame(tick);
        }
      };
      requestAnimationFrame(tick);
      await new Promise((resolve, reject) => {
        video.addEventListener("ended", resolve, { once: true });
        setTimeout(() => reject(new Error("Extraction timed out (5 min max)")), 5 * 60 * 1000);
      });
      recorder.stop();
      await new Promise((r) => (recorder.onstop = r));
      URL.revokeObjectURL(url);

      const blob = new Blob(chunks, { type: mime.split(";")[0] });
      const audioFile = new File(
        [blob],
        (file.name || "extracted").replace(/\.[^.]+$/, "") + ".webm",
        { type: mime.split(";")[0] }
      );
      onPick(audioFile, `extracted from ${file.name}`);
      toast.success("Audio extracted");
      setProgress(1);
    } catch (e) {
      toast.error(e.message || "Extraction failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        data-testid="extract-video-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) extract(f);
        }}
      />
      <button
        type="button"
        data-testid="extract-video-btn"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        className="w-full border-2 border-dashed border-white/15 hover:border-[#FFB800]/60 hover:bg-[#FFB800]/5 py-6 flex flex-col items-center gap-1 transition-all disabled:opacity-50 group"
      >
        {busy ? (
          <>
            <Loader2 className="h-6 w-6 text-[#FFB800] animate-spin" strokeWidth={1.5} />
            <p className="text-white/60 text-sm">extracting… {Math.round(progress * 100)}%</p>
          </>
        ) : (
          <>
            <VideoIcon className="h-6 w-6 text-white/40 group-hover:text-[#FFB800]" strokeWidth={1.5} />
            <p className="text-white/60 text-sm">pick a video to extract its audio</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/30">
              plays.through.once · webm/opus · max 5 min
            </p>
          </>
        )}
      </button>
    </>
  );
}

/* ---------------------------- Library tab ---------------------------- */
function LibraryTab({ onPick }) {
  const [tracks, setTracks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [genre, setGenre] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [fetchingId, setFetchingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (genre) params.set("genre", genre);
    if (q) params.set("q", q);
    params.set("limit", "50");
    api.get(`/music/library?${params.toString()}`)
      .then((r) => {
        setTracks(r.data?.tracks || []);
        setGenres(r.data?.genres || []);
      })
      .catch(() => setTracks([]))
      .finally(() => setLoading(false));
  }, [genre, q]);

  // Stop preview audio when the LibraryTab unmounts (e.g. user switches tabs).
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const togglePreview = (t) => {
    if (previewId === t.id) {
      audioRef.current?.pause();
      setPreviewId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(resolveTrackUrl(t.url));
    audio.crossOrigin = "anonymous";
    audio.play().catch(() => toast.error("Preview blocked by browser"));
    audio.onended = () => setPreviewId(null);
    audioRef.current = audio;
    setPreviewId(t.id);
  };

  const pickTrack = async (t) => {
    if (audioRef.current) audioRef.current.pause();
    setPreviewId(null);
    setFetchingId(t.id);
    try {
      const res = await fetch(resolveTrackUrl(t.url));
      if (!res.ok) throw new Error(`Fetch ${res.status}`);
      const blob = await res.blob();
      const filename = `${t.title.replace(/[^a-z0-9]+/gi, "_")}.mp3`;
      const file = new File([blob], filename, { type: blob.type || "audio/mpeg" });
      onPick(file, `${t.title} · ${t.artist}`, { trackId: t.id });
      setSelectedId(t.id);
      toast.success(`Selected: ${t.title}`);
    } catch {
      toast.error("Could not attach — track download blocked. Try a different track or upload.");
    } finally {
      setFetchingId(null);
    }
  };

  return (
    <div data-testid="music-library">
      {/* Search + genre chips */}
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" strokeWidth={2} />
          <input
            data-testid="music-library-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search title, artist, genre…"
            className="w-full bg-black border border-white/10 focus:border-[#00FFFF]/40 focus:outline-none text-white text-sm pl-8 pr-3 py-2"
          />
        </div>
        {genre && (
          <button
            type="button"
            data-testid="music-library-clear-genre"
            onClick={() => setGenre("")}
            className="px-3 border border-[#FF007F]/40 text-[#FF007F] font-mono text-[10px] uppercase tracking-widest"
          >
            ✕ {genre}
          </button>
        )}
      </div>
      {!genre && genres.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {genres.map((g) => (
            <button
              type="button"
              key={g}
              data-testid={`music-genre-${g}`}
              onClick={() => setGenre(g)}
              className="px-2.5 py-1 border border-white/10 hover:border-[#00FFFF]/60 hover:text-[#00FFFF] font-mono text-[9px] uppercase tracking-widest text-white/60 transition-colors"
            >
              {g}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="py-8 flex items-center justify-center">
          <Loader2 className="h-4 w-4 text-[#00FFFF] animate-spin" />
        </div>
      ) : tracks.length === 0 ? (
        <div className="py-8 text-center">
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            no tracks match
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-white/5 border border-white/10 max-h-[320px] overflow-y-auto">
          {tracks.map((t) => {
            const isSelected = selectedId === t.id;
            const isPlaying = previewId === t.id;
            const isFetching = fetchingId === t.id;
            return (
              <li
                key={t.id}
                data-testid={`music-track-${t.id}`}
                className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${
                  isSelected ? "bg-[#00FFFF]/10" : "hover:bg-white/[0.02]"
                }`}
              >
                <button
                  type="button"
                  data-testid={`music-preview-${t.id}`}
                  onClick={() => togglePreview(t)}
                  className="h-8 w-8 border border-white/15 hover:border-[#00FFFF] text-white/70 hover:text-[#00FFFF] flex items-center justify-center flex-shrink-0"
                  title={isPlaying ? "Pause" : "Preview"}
                >
                  {isPlaying ? <Pause className="h-3.5 w-3.5" fill="currentColor" /> : <Play className="h-3.5 w-3.5" fill="currentColor" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{t.title}</p>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                    {t.artist} · {t.genre} · {Math.floor(t.duration_sec / 60)}:{String(t.duration_sec % 60).padStart(2, "0")}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`music-pick-${t.id}`}
                  onClick={() => pickTrack(t)}
                  disabled={isFetching || isSelected}
                  className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest transition-all flex items-center gap-1 ${
                    isSelected
                      ? "border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]"
                      : "border-[#00FFFF]/60 text-[#00FFFF] hover:bg-[#00FFFF]/10"
                  } disabled:opacity-70`}
                >
                  {isFetching ? (
                    <><Loader2 className="h-3 w-3 animate-spin" /> loading</>
                  ) : isSelected ? (
                    <><Check className="h-3 w-3" strokeWidth={2.5} /> picked</>
                  ) : (
                    "use"
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-2 font-mono text-[9px] uppercase tracking-widest text-white/30 text-center">
        // demo.library · royalty-free · larger catalog coming w/ Pixabay Music
      </p>
    </div>
  );
}
